import { SouthEastOutlined, SouthWestOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { IntlShape } from 'react-intl';
import { assign } from 'xstate';

import { MachineContext } from '../../MachineContext';
import { SendContext } from '../../SendContext';
import { translateWithFallback } from '../../util';
import { EvoachButton } from '../Button';
import { BaseComponent } from '../Component';

export interface BinarySorterComponent extends BaseComponent {
  type: 'binarySorter';
  props: BinarySorterProps;
}

export interface BinarySorterItem {
  type: string;
  value: string;
  isLeft: boolean;
  isBlindSpot?: boolean;
}
export interface BinarySorterProps {
  elements: BinarySorterItem[];
  buttonText: string;
  saveResultTo: string;
  leftTitle?: string;
  rightTitle?: string;
  blindSpots?: boolean;
  arrows?: boolean;
  isEcho?: boolean;
}

// a little function to help us with reordering the result
const reorder = (
  list: BinarySorterItem[],
  droppableSource: any,
  droppableDestination: any
): BinarySorterItem[] => {
  const result = Array.from(list);
  const [removed] = result.splice(droppableSource, 1);
  result.splice(droppableDestination, 0, removed);
  return result;
};

export const deleteBlindSpots = (
  elements: BinarySorterItem[]
): BinarySorterItem[] => {
  return Array.from(elements).filter((element) => !element.isBlindSpot);
};

export const addBlindSpots = (
  elements: BinarySorterItem[]
): BinarySorterItem[] => {
  let result: BinarySorterItem[];
  const isEmptyArray: boolean =
    Array.isArray(elements) && elements.length === 0;
  if (
    elements === undefined ||
    elements === null ||
    !Array.isArray(elements) ||
    isEmptyArray
  ) {
    result = [];
    result.splice(0, 0, {
      type: 'blindSpot',
      value: '...',
      isLeft: true,
      isBlindSpot: true,
    });
    result.splice(1, 0, {
      type: 'blindSpot',
      value: '...',
      isLeft: false,
      isBlindSpot: true,
    });
    return result;
  }
  result = Array.from(elements).filter((element) => !element.isBlindSpot);
  let index = 0;

  if (!result[0].isLeft) {
    result.splice(0, 0, {
      type: 'blindSpot',
      value: '...',
      isLeft: true,
      isBlindSpot: true,
    });
    index++;
  }

  while (result[index + 1]) {
    if (result[index].isLeft === result[index + 1].isLeft) {
      result.splice(index + 1, 0, {
        type: 'blindSpot',
        value: '...',
        isLeft: !result[index].isLeft,
        isBlindSpot: true,
      });
    }
    index++;
  }

  if (result[index].isLeft) {
    result.splice(index + 1, 0, {
      type: 'blindSpot',
      value: '...',
      isLeft: false,
      isBlindSpot: true,
    });
  }
  return result;
};

const calculateBlindSpotReturns = (
  elements: BinarySorterItem[]
): BinarySorterItem[] => {
  let list = Array.from(elements);
  let result = [];
  for (let i = 0; i < list.length; i++) {
    if (list[i].isBlindSpot) {
      if (list[i].isLeft) {
        const item = list[i + 1];
        item.type = 'blindSpot';
        result.push(item);
      } else {
        const item = list[i - 1];
        item.type = 'blindSpot';
        result.push(item);
      }
    }
  }
  return result;
};

export const BinarySorter: React.FC<BinarySorterProps> = ({
  elements,
  buttonText,
  saveResultTo,
  leftTitle,
  rightTitle,
  blindSpots,
  arrows,
  isEcho,
}) => {
  const { send } = useContext(SendContext);
  const binarySorterID = useMemo(() => {
    return 'binarySorter' + isEcho + Math.round(Math.random() * 100000000);
  }, [isEcho]);

  const [items, setItems] = useState<BinarySorterItem[]>(
    elements ? elements.flat() : []
  );
  const [dropStyle, setDropStyle] = useState<any>({
    backgroundColor: '#fff8e4',
    minHeight: 200,
    maxHeight: 200,
    minWidth: '250px',
  });

  useEffect(() => {
    const blindSpotHeight = blindSpots ? elements.length * 56 : 0;
    const droppableHeight = document.getElementById('droppable')?.scrollHeight;
    if (blindSpots) {
      setItems(addBlindSpots(elements.flat()));
    }
    let currentHeight =
      droppableHeight !== undefined
        ? droppableHeight + blindSpotHeight
        : undefined;
    if (!currentHeight) {
      const obj = document.getElementById('droppable');
      currentHeight = obj ? obj.scrollHeight + blindSpotHeight : 200;
    }
    if (currentHeight) {
      const getDroppableStyle = {
        backgroundColor: '#fff8e4',
        minHeight: currentHeight + 16,
        maxHeight: currentHeight + 16,
        minWidth: '250px',
      };
      setDropStyle(getDroppableStyle);
    }
  }, []);

  let draggedId = '';
  const dragEnd = (result: any) => {
    draggedId = '';
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    const dragResult: any = reorder(items, source.index, destination.index);

    if (!(source.droppableId === destination.droppableId)) {
      let index =
        destination.index >= items.length
          ? items.length - 1
          : destination.index;
      dragResult[index].isLeft = !dragResult[index].isLeft;
    }
    let newElements = dragResult;
    if (blindSpots) {
      newElements = deleteBlindSpots(newElements);
      newElements = addBlindSpots(newElements);
    }
    setItems(newElements);
  };
  const onDragStart = (result: any) => {
    const source = result.source;
    const item = items[source.index];
    let idSuffix = '-' + item.type + '-' + item.value + '-' + source.index;
    draggedId = item.isLeft
      ? 'droppableLeft' + idSuffix
      : 'droppableRight' + idSuffix;
  };
  return (
    <div key={binarySorterID + 'surroundingdiv'}>
      <div
        style={{ display: 'flex' }}
        key={binarySorterID + 'surroundingInnerdiv'}
      >
        <DragDropContext onDragEnd={dragEnd} onDragStart={onDragStart}>
          <Droppable droppableId="droppableLeft">
            {(droppableProvided: any, dropableSnapshot) => (
              <div
                id="droppable"
                ref={droppableProvided.innerRef}
                style={{ ...dropStyle, backgroundColor: '#fff8e4' }}
              >
                <Typography
                  variant="h5"
                  style={{ textAlign: 'center', fontWeight: 'bold' }}
                >
                  {leftTitle}
                </Typography>
                {items.map((item, index) => {
                  return (
                    <Draggable
                      key={`${item.type}-${item.value}-${index}`}
                      draggableId={`droppableLeft-${binarySorterID}-${item.type}-${item.value}-${index}`}
                      index={index}
                      isDragDisabled={isEcho}
                    >
                      {(draggableProvided, draggableSnapshot) => (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box
                            sx={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.5rem',
                              userSelect: 'none',
                              margin: '8px',
                              borderRadius: '6px',
                              backgroundColor: item.isBlindSpot
                                ? 'white'
                                : draggableSnapshot.isDragging
                                ? dropableSnapshot.isDraggingOver
                                  ? '#ffde77'
                                  : '#7ab7c4'
                                : '#ffde77',
                              visibility: item.isLeft ? 'visible' : 'hidden',
                              minWidth: '200px',
                              width: '100%',
                              display:
                                `droppableRight-${item.type}-${item.value}-${index}` ===
                                draggedId
                                  ? 'none'
                                  : 'flex',
                            }}
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                          >
                            <Typography>{item.value}</Typography>
                          </Box>
                          <SouthWestOutlined
                            sx={{
                              display:
                                arrows &&
                                !item.isLeft &&
                                index < items.length - 1
                                  ? 'auto'
                                  : 'none',
                              position: 'relative',
                              top: '1rem',
                            }}
                          />
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
          <Droppable droppableId="droppableRight">
            {(droppableProvided: any, dropableSnapshot) => (
              <div
                id="droppable"
                ref={droppableProvided.innerRef}
                style={{ ...dropStyle, backgroundColor: '#edf7f9' }}
              >
                <Typography
                  variant="h5"
                  style={{ textAlign: 'center', fontWeight: 'bold' }}
                >
                  {rightTitle}
                </Typography>
                {items.map((item, index) => {
                  return (
                    <Draggable
                      key={`${item.type}-${item.value}-${index}`}
                      draggableId={`droppableRight-${binarySorterID}-${item.type}-${item.value}-${index}`}
                      index={index}
                      isDragDisabled={isEcho}
                    >
                      {(draggableProvided, draggableSnapshot) => (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <SouthEastOutlined
                            sx={{
                              display:
                                arrows &&
                                item.isLeft &&
                                index < items.length - 1
                                  ? 'auto'
                                  : 'none',
                              position: 'relative',
                              top: '1rem',
                            }}
                          />
                          <Box
                            sx={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.5rem',
                              userSelect: 'none',
                              margin: '8px',
                              borderRadius: '6px',
                              backgroundColor: item.isBlindSpot
                                ? 'white'
                                : draggableSnapshot.isDragging
                                ? dropableSnapshot.isDraggingOver
                                  ? '#7ab7c4'
                                  : '#ffde77'
                                : '#7ab7c4',
                              visibility: item.isLeft ? 'hidden' : 'visible',
                              minWidth: '200px',
                              width: '100%',
                              display:
                                `droppableLeft-${item.type}-${item.value}-${index}` ===
                                draggedId
                                  ? 'none'
                                  : 'flex',
                            }}
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                          >
                            <Typography>{item.value}</Typography>
                          </Box>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {!isEcho ? (
        <EvoachButton
          onClick={() => {
            send({
              type: 'writeToContext',
              payload: {
                [saveResultTo]: items,
              },
            });
            send({
              type: 'writeToContext',
              payload: {
                [saveResultTo + '_blindSpots']: calculateBlindSpotReturns(
                  items
                ),
              },
            });
            send({
              type: 'writeToContext',
              payload: {
                [saveResultTo + '_leftItems']: items.filter(
                  (element) => element.isLeft
                ),
              },
            });
            send({
              type: 'writeToContext',
              payload: {
                [saveResultTo + '_rightItems']: items.filter(
                  (element) => !element.isLeft
                ),
              },
            });
            send({ type: 'next' });
          }}
        >
          {buttonText}
        </EvoachButton>
      ) : (
        ''
      )}
    </div>
  );
};

export const renderBinarySorterAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;

    let elements = payload.elements ?? [];

    // if there are variables, check them for content
    if (payload.getValuesFrom && Array.isArray(payload.getValuesFrom)) {
      elements = [];
      // check content for each variable
      payload.getValuesFrom.forEach((variablename: string) => {
        //
        const content = context.userData[variablename];
        // we may pass an array of strings ...
        if (content !== undefined) {
          if (Array.isArray(content) && typeof content[0] === 'string') {
            content.forEach((element: string) => {
              elements.push({
                type: 'unknown',
                value: element,
                isLeft: true,
              });
            });
          } else {
            // ... or we may pass an array of objects
            content.forEach((element: Record<string, any>) => {
              elements.push({ ...element, isLeft: element.isLeft ?? false });
            });
          }
        }
      });
    }

    newContext.widgetData.push({
      type: 'binarySorter',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        leftTitle: translateWithFallback(intl, payload.leftTitle),
        rightTitle: translateWithFallback(intl, payload.rightTitle),
        blindSpots: !!payload.blindSpots,
        arrows: !!payload.arrows,
        isEcho: !!payload.isEcho,
      },
    });
    return newContext;
  });
};
